import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { ContentId } from 'src/utils/constants/contentId';
import {
  SellerAccountClient,
  SellerUserAccountInfo,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useContent } from '../ContentContext';

type ISellerAccountContext = {
  allUserInfos: Record<string, SellerUserAccountInfo> | null; // List of all user-infos for the seller account (including deleted ones so they can be displayed on entity's ownership)
  allActiveUserInfos: Record<string, SellerUserAccountInfo> | null; // List of all active user-infos for the seller account
  allUserInfosAllAccounts: SellerUserAccountInfo[] | null | undefined; // List of all user-infos for all accounts including consignee accounts
  isLoading?: boolean;
  getUserInfo: (id: string) => SellerUserAccountInfo | null;
  refetchUserInfos: () => void;
};

export const SellerAccountContext = createContext<ISellerAccountContext>({
  allUserInfos: {},
  allActiveUserInfos: {},
  allUserInfosAllAccounts: [],
  isLoading: false,
  getUserInfo: () => null,
  refetchUserInfos: () => {},
});

export const useSellerAccountContext = () => useContext(SellerAccountContext);

export const SELLER_ACCOUNT_SELLER_USERS_QUERY_KEY =
  'SellerAccountClient.getSellerUsersForSellerAccount';

/**
 * Adds a modal to the page where the provider is added
 * Provides utils to set all or update part of the modal
 * The modal is assumed to be open when it contains content
 */
export const SellerAccountContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const deactivatedText = useContent(ContentId.Deactivated);

  const getQuery = useQuery({
    queryKey: [
      SELLER_ACCOUNT_SELLER_USERS_QUERY_KEY,
      activeAccountWebClientConfig.activeAccountId,
    ],
    queryFn: async () => {
      if (activeAccountWebClientConfig.activeAccountId == null) {
        return null;
      }
      const results = await new SellerAccountClient(
        activeAccountWebClientConfig
      ).getSellerUsersForSellerAccount();

      return results;
    },

    enabled: activeAccountWebClientConfig.activeAccountId != null,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('SellerAccountClient.getSellerUsersForSellerAccount', error);
      },
    },
  });

  const getQueryResultCurrentAccount = useMemo(() => {
    const results = getQuery.data;

    const resultsMap = results
      ?.filter(
        (r) =>
          r.consigneeSellerAccountId == null ||
          r.consigneeSellerAccountId ===
            activeAccountWebClientConfig.activeAccountId
      )
      .reduce<Record<string, SellerUserAccountInfo>>((r, cur) => {
        r[cur.userId] = cur;
        return r;
      }, {});

    return resultsMap;
  }, [getQuery.data, activeAccountWebClientConfig.activeAccountId]);

  const getUserInfo = useCallback(
    (userId: string) => {
      if (!userId || !getQueryResultCurrentAccount) {
        return null;
      }

      return getQueryResultCurrentAccount[userId];
    },
    [getQueryResultCurrentAccount]
  );

  const allUserInfos = useMemo(() => {
    if (!getQueryResultCurrentAccount) return null;

    return Object.values(getQueryResultCurrentAccount).reduce<
      Record<string, SellerUserAccountInfo>
    >((r, cur) => {
      if (cur.isDeactivated) {
        cur.displayName = `${cur.displayName} (${deactivatedText})`;
      }
      r[cur.userId] = cur;
      return r;
    }, {});
  }, [deactivatedText, getQueryResultCurrentAccount]);

  const allActiveUserInfos = useMemo(() => {
    if (!getQueryResultCurrentAccount) return null;

    return Object.values(getQueryResultCurrentAccount)
      .filter((u) => !u.isDeactivated)
      .reduce<Record<string, SellerUserAccountInfo>>((r, cur) => {
        r[cur.userId] = cur;
        return r;
      }, {});
  }, [getQueryResultCurrentAccount]);

  return (
    <SellerAccountContext.Provider
      value={{
        allUserInfos: allUserInfos,
        allActiveUserInfos: allUserInfos,
        allUserInfosAllAccounts: getQuery.data,
        isLoading: getQuery.isLoading,
        getUserInfo: getUserInfo,
        refetchUserInfos: getQuery.refetch,
      }}
    >
      {children}
    </SellerAccountContext.Provider>
  );
};
